/**
 * FirstLogin — forced password change after admin provisioning.
 *
 * Triggered by ``user.must_reset_password === true`` on the user
 * object the backend returns from /auth/login or /auth/validate. The
 * route guard in App.js redirects every protected navigation here
 * until the user actually picks a new password.
 *
 * Why it's a dedicated page (vs. a modal): a modal can be closed
 * with Esc or clicked-outside, which would leave a half-authenticated
 * user staring at a real page. A standalone route + a guard that
 * keeps redirecting back here is uncloseable by design.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FirstLogin() {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Client-side rules. The backend enforces length ≥ 8; we add a
  // few more so users can't ship something like "Password" and feel
  // safe. The rule list also gives the form something useful to
  // render below the inputs.
  const rules = [
    { label: 'At least 8 characters', ok: newPassword.length >= 8 },
    { label: 'Contains a number', ok: /\d/.test(newPassword) },
    { label: 'Contains a letter', ok: /[a-zA-Z]/.test(newPassword) },
    { label: 'New password differs from current', ok: newPassword.length > 0 && newPassword !== currentPassword },
    { label: 'Confirmation matches', ok: confirmPassword.length > 0 && newPassword === confirmPassword },
  ];
  const allOk = rules.every((r) => r.ok);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allOk) {
      toast.error('Please satisfy every rule before submitting');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/users/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { params: { token } },
      );
      // Optimistically clear the flag in our local user state so the
      // route guard releases us immediately — the next session
      // validate roundtrip will confirm.
      if (setUser && user) {
        const updated = { ...user, must_reset_password: false };
        setUser(updated);
        try {
          sessionStorage.setItem('user', JSON.stringify(updated));
        } catch (_) { /* sessionStorage is best-effort */ }
      }
      toast.success('Password updated — welcome to the CRM');
      // Land on the dashboard. For BDM users the App.js guard will
      // re-route them to /leads-dashboard/brokerage-hub.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not update password';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-violet-50 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-violet-100">
            <ShieldCheck className="h-6 w-6 text-violet-600" />
          </div>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>
            Your account was set up with a temporary password. Please choose a new
            one before continuing — you'll use this every time you log in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertCircle className="mr-1.5 inline h-3.5 w-3.5" />
            You're signed in as <strong>{user?.email}</strong>. This step is one-time.
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <Field
              label="Current (temporary) password"
              value={currentPassword}
              onChange={setCurrentPassword}
              show={showCurrent}
              onToggleShow={() => setShowCurrent((v) => !v)}
              autoComplete="current-password"
              autoFocus
            />
            <Field
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              autoComplete="new-password"
            />
            <Field
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              autoComplete="new-password"
            />

            <ul className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px]">
              {rules.map((r) => (
                <li
                  key={r.label}
                  className={`flex items-center gap-1.5 ${r.ok ? 'text-emerald-700' : 'text-slate-500'}`}
                >
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.ok ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  {r.label}
                </li>
              ))}
            </ul>

            <Button
              type="submit"
              className="w-full bg-violet-600 hover:bg-violet-700"
              disabled={!allOk || submitting}
            >
              {submitting ? 'Updating…' : 'Set new password & continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const Field = ({ label, value, onChange, show, onToggleShow, ...rest }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative mt-1">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9 pr-9"
        {...rest}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  </div>
);
